import React, { useState, useRef } from "react";
import { useCompleteProfile } from "@/context/CompleteProfileContext";
import CompleteProfileFooter from "../CompleteProfileFooter";
import { BioEditor, BioEditorValidationResult, BioEditorRef } from "@/components/shared/BioEditor";
import { FileText, Sparkles, ArrowRight } from "lucide-react";
import { BIO_MIN_LENGTH } from "@/core/BusinessRulesEngine";

const BioStep = () => {
  const { data, updateData, nextStep, saving } = useCompleteProfile();
  const [hasValidated, setHasValidated] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const bioEditorRef = useRef<BioEditorRef>(null);

  const handleBioChange = (value: string) => {
    updateData({ bio: value });
    setHasValidated(false); // Reset when bio changes
  };

  const handleValidationStart = () => {
    setIsValidating(true);
  };

  const handleValidationComplete = (result: BioEditorValidationResult) => {
    setIsValidating(false);
    setHasValidated(true);
    updateData({ 
      bio: result.bio,
      bio_topics: result.topics,
      bio_score: result.score
    });
  };

  const handleButtonClick = () => {
    if (hasValidated) {
      nextStep(); // Already validated: proceed
    } else {
      bioEditorRef.current?.triggerValidation(); // Not validated: trigger validation
    }
  };

  const bioLength = data.bio?.length || 0;
  const canClick = bioLength >= BIO_MIN_LENGTH;

  // Dynamic button text and icon
  const getButtonContent = () => {
    if (isValidating) {
      return { text: "Validando com IA...", icon: null };
    }
    if (hasValidated) {
      return { text: "Continuar", icon: <ArrowRight className="w-4 h-4 ml-2" /> };
    }
    return { text: "Validar com IA", icon: <Sparkles className="w-4 h-4 ml-2" /> };
  };

  const buttonContent = getButtonContent();

  return (
    <CompleteProfileFooter
      buttonText={buttonContent.text}
      onClick={handleButtonClick}
      disabled={!canClick}
      isLoading={isValidating || saving}
      loadingText="Validando com IA..."
      buttonIcon={buttonContent.icon}
      buttonVariant={hasValidated ? "primary" : "secondary"}
    >
      <div className="space-y-6">
        {/* Header */}
        <div className="text-center space-y-2">
          <div className="w-16 h-16 rounded-full bg-luxury-pinkBright/20 flex items-center justify-center mx-auto mb-4">
            <FileText className="w-8 h-8 text-luxury-pinkBright" />
          </div>
          <h2 className="text-2xl font-bold text-white">Sua Bio</h2>
          <p className="text-white/60 text-sm">
            Conte um pouco sobre você para atrair as pessoas certas
          </p>
        </div>

        {/* Bio Editor with AI validation - button hidden, controlled by footer */}
        <BioEditor
          ref={bioEditorRef}
          initialBio={data.bio || ""}
          onBioChange={handleBioChange}
          onValidationStart={handleValidationStart}
          onValidationComplete={handleValidationComplete}
          occupation={data.occupation || undefined}
          education={data.education || undefined}
          showTopicsPreview={true}
          showSaveButton={false}
          showValidateButton={false}
        />
      </div>
    </CompleteProfileFooter>
  );
};

export default BioStep;
